/**
 * Account settings for the signed-in user.
 *
 * The previous version stacked two forms in a narrow column and rendered every
 * session as its raw user-agent string — twenty-eight rows of
 * `Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36…`, truncated
 * mid-token. A session list exists so someone can recognise a sign-in they did
 * not make, and identical unreadable strings make that impossible.
 *
 * So: the identity is summarised at the top, the two actions sit side by side
 * rather than one below the other, and each session is described by device and
 * browser with the raw string kept on hover.
 *
 * One thing deliberately absent: revoking a single session. The API returns no
 * per-session identifier, so the only honest controls are "change password"
 * (which ends the others) and "sign out everywhere". Rendering a per-row
 * button that could not work would be worse than not having one.
 */

import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert, Badge, Button, Field, Input, Panel, SectionHeader, Skeleton,
} from '@/components/ui/primitives'
import { accountApi } from '@/services/endpoints'
import { ApiError } from '@/services/client'
import { formatDateTime, formatRelative } from '@/utils/format'
import { describeAgent, looksLikeThisDevice } from '@/utils/useragent'
import { useAuth } from '@/stores/auth'

/** Rows shown before the list collapses. Enough to scan, not a wall. */
const VISIBLE_SESSIONS = 6

export function AccountPage() {
  const { user, logout } = useAuth()
  const queryClient = useQueryClient()

  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [done, setDone] = useState(false)
  const [showAll, setShowAll] = useState(false)
  const [confirmSignOut, setConfirmSignOut] = useState(false)

  const sessions = useQuery({ queryKey: ['account', 'sessions'], queryFn: accountApi.sessions })

  const change = useMutation({
    mutationFn: () => accountApi.changePassword(current, next),
    onSuccess: () => {
      setCurrent('')
      setNext('')
      setDone(true)
      void queryClient.invalidateQueries({ queryKey: ['account'] })
    },
  })

  const revokeAll = useMutation({
    mutationFn: accountApi.revokeAllSessions,
    onSuccess: () => void logout(),
  })

  const error = change.error instanceof ApiError ? change.error : null

  const rows = useMemo(() => {
    const list = [...(sessions.data ?? [])]
    // Newest first: the sign-in someone is trying to recognise is the one that
    // just happened, not the one from three weeks ago.
    list.sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at))
    return list.map((session) => ({
      ...session,
      agent: describeAgent(session.user_agent),
      isThisDevice: looksLikeThisDevice(session.user_agent),
    }))
  }, [sessions.data])

  const visible = showAll ? rows : rows.slice(0, VISIBLE_SESSIONS)

  return (
    <div className="px-4 py-10 sm:px-6">
      {/* -- Who ---------------------------------------------------------- */}
      <div className="flex items-center gap-4">
        <span className="flex size-12 shrink-0 items-center justify-center rounded-full bg-[var(--accent-subtle)] text-base font-semibold text-[var(--accent-text)]">
          {user?.first_name?.[0]}
          {user?.last_name?.[0]}
        </span>
        <div className="min-w-0">
          <h1 className="truncate text-xl font-semibold text-[var(--text-primary)]">
            {user?.full_name || user?.email}
          </h1>
          <p className="mt-0.5 flex flex-wrap items-center gap-2 text-sm text-[var(--text-secondary)]">
            <span className="truncate">{user?.email}</span>
            {user?.role ? (
              <Badge tone="accent" size="xs">
                {user.role.replace(/_/g, ' ')}
              </Badge>
            ) : null}
          </p>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* -- Password --------------------------------------------------- */}
        <Panel>
          <SectionHeader
            title="Change password"
            description="This ends every other session, on every device."
          />

          <form
            className="mt-4 space-y-4"
            onSubmit={(event) => {
              event.preventDefault()
              setDone(false)
              change.mutate()
            }}
          >
            <Field label="Current password" htmlFor="current" required>
              <Input
                id="current"
                type="password"
                autoComplete="current-password"
                value={current}
                onChange={(event) => setCurrent(event.target.value)}
                required
              />
            </Field>

            <Field
              label="New password"
              htmlFor="next"
              required
              hint="At least 10 characters. Not only numbers."
            >
              <Input
                id="next"
                type="password"
                autoComplete="new-password"
                value={next}
                onChange={(event) => setNext(event.target.value)}
                required
                minLength={10}
              />
            </Field>

            {error ? (
              <Alert tone="negative">
                {error.message}
                {/* The API returns every rule that failed, so show all of
                    them rather than making someone guess one at a time. */}
                {Object.values(error.fields).flat().length > 0 ? (
                  <ul className="mt-1.5 list-disc pl-4">
                    {Object.values(error.fields).flat().map((problem) => (
                      <li key={problem}>{problem}</li>
                    ))}
                  </ul>
                ) : null}
              </Alert>
            ) : null}

            {done ? <Alert tone="positive">Password changed.</Alert> : null}

            <Button type="submit" disabled={change.isPending || !current || !next}>
              {change.isPending ? 'Changing…' : 'Change password'}
            </Button>
          </form>
        </Panel>

        {/* -- Sessions --------------------------------------------------- */}
        <Panel>
          <SectionHeader
            title="Active sessions"
            description={
              rows.length
                ? `${rows.length} sign-in${rows.length === 1 ? '' : 's'} currently valid.`
                : 'Where this account is currently signed in.'
            }
          />

          {sessions.isLoading ? (
            <div className="mt-4 space-y-2">
              {Array.from({ length: 4 }).map((_, index) => (
                <Skeleton key={index} className="h-10" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <p className="mt-4 text-sm text-[var(--text-tertiary)]">
              No other sessions.
            </p>
          ) : (
            <>
              <ul className="mt-4 divide-hairline">
                {visible.map((session, index) => (
                  <li
                    key={`${session.created_at}-${index}`}
                    className="flex items-start justify-between gap-3 py-2.5"
                  >
                    <div className="min-w-0">
                      <p className="flex items-center gap-2 text-sm text-[var(--text-primary)]">
                        {/* The raw string stays reachable for the cases the
                            parser gets wrong. */}
                        <span title={session.user_agent ?? undefined}>
                          {session.agent.label}
                        </span>
                        {session.isThisDevice ? (
                          <Badge tone="accent" size="xs">
                            This device
                          </Badge>
                        ) : null}
                      </p>
                      <p className="text-2xs text-[var(--text-tertiary)]">
                        {formatRelative(session.created_at)}
                        {session.ip_address ? ` · ${session.ip_address}` : ''}
                        {' · expires '}
                        {formatDateTime(session.expires_at)}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>

              {rows.length > VISIBLE_SESSIONS ? (
                <button
                  type="button"
                  onClick={() => setShowAll(!showAll)}
                  className="mt-2 text-xs text-[var(--accent-text)] hover:underline"
                >
                  {showAll
                    ? 'Show fewer'
                    : `Show all ${rows.length} sessions`}
                </button>
              ) : null}
            </>
          )}

          <div className="mt-5 border-t border-[var(--border-subtle)] pt-4">
            {confirmSignOut ? (
              <div className="space-y-3">
                <Alert tone="caution">
                  This signs you out on every device, including this one. You
                  will need to sign in again.
                </Alert>
                <div className="flex gap-2">
                  <Button
                    variant="danger"
                    size="sm"
                    disabled={revokeAll.isPending}
                    onClick={() => revokeAll.mutate()}
                  >
                    {revokeAll.isPending ? 'Signing out…' : 'Yes, sign out everywhere'}
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setConfirmSignOut(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setConfirmSignOut(true)}
              >
                Sign out everywhere
              </Button>
            )}
          </div>
        </Panel>
      </div>

      <p className="mt-4 max-w-2xl text-2xs leading-relaxed text-[var(--text-tertiary)]">
        Sessions are recorded server-side, so signing out here genuinely ends
        them — a token cannot outlive its record. Addresses are stored with the
        final part masked; the index does not need to know precisely where you
        sign in from.
      </p>
    </div>
  )
}
